import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { ConfirmDialog, ContentHeader, Field, Modal, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type { StatusBoard, StatusInput, StatusView, TransitionView } from "@cx/contracts";
import {
  ACTION_LABELS,
  BASE_STATES,
  BASE_STATE_LABELS,
  STATUS_MODULES,
  STATUS_MODULE_LABELS,
  STATUS_TONES,
  type StatusModule,
  type TransitionAction,
} from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { describeActions, offeredActions, TONE_LABELS } from "./labels";

type Editing = { id?: string; isSystem: boolean; values: StatusInput };
const EMPTY: StatusInput = { label: "", color: "secondary", baseState: "open", position: 0 };

/**
 * Preferências › Situações e transições (RF-PRF-03, RF-PRF-12): situações de cada módulo (as de sistema e as
 * customizadas) e o grafo de transições com as ações que reservam, lançam e estornam estoque e contas.
 */
export function StatusesPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const perms = me.permissions as Permissions;
  const [module, setModule] = useState<StatusModule>("sales_orders");
  const key = ["statuses", companyId, module];
  const board = useQuery({
    queryKey: key,
    queryFn: () => api<StatusBoard>(`/statuses?module=${module}`),
  });
  const [editing, setEditing] = useState<Editing | null>(null);
  const [editingTransition, setEditingTransition] = useState<TransitionView | "new" | null>(null);
  const [deleting, setDeleting] = useState<{
    kind: "status" | "transition";
    id: string;
    label: string;
  } | null>(null);
  const changed = () => qc.invalidateQueries({ queryKey: ["statuses", companyId] });
  const del = useMutation({
    mutationFn: (d: NonNullable<typeof deleting>) =>
      api(d.kind === "status" ? `/statuses/${d.id}` : `/status-transitions/${d.id}`, { method: "DELETE" }),
    onSuccess: (_r, d) => {
      toast("success", d.kind === "status" ? "Situação excluída" : "Transição excluída");
      changed();
      qc.invalidateQueries({ queryKey: ["deletions", companyId] });
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const statuses = board.data?.statuses ?? [];
  const byId = new Map(statuses.map((s) => [s.id, s]));
  const badge = (id: string) => {
    const s = byId.get(id);
    return s ? <StatusBadge tone={s.color}>{s.label}</StatusBadge> : <span className="text-muted">—</span>;
  };
  const pairLabel = (t: TransitionView) =>
    `${byId.get(t.fromStatusId)?.label ?? "—"} → ${byId.get(t.toStatusId)?.label ?? "—"}`;
  const transitions = [...(board.data?.transitions ?? [])].sort(
    (a, b) =>
      (byId.get(a.fromStatusId)?.position ?? 0) - (byId.get(b.fromStatusId)?.position ?? 0) ||
      (byId.get(a.toStatusId)?.position ?? 0) - (byId.get(b.toStatusId)?.position ?? 0),
  );
  return (
    <>
      <ContentHeader
        title="Situações e transições"
        breadcrumb={["Preferências", "Situações e transições"]}
        actions={
          can(perms, "statuses", "create") && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={!board.isSuccess}
              onClick={() => setEditing({ isSystem: false, values: EMPTY })}
            >
              Incluir situação
            </button>
          )
        }
      />
      <div className="row mb-2">
        <Field label="Módulo" className="col-md-4">
          <select
            className="form-select"
            value={module}
            onChange={(e) => setModule(e.target.value as StatusModule)}
          >
            {STATUS_MODULES.map((m) => (
              <option key={m} value={m}>
                {STATUS_MODULE_LABELS[m]}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="box card mb-3">
        <div className="px-3 pt-3 pb-2">
          <h2 className="h6 mb-0">Situações</h2>
        </div>
        <QueryStatus loading={board.isLoading} error={board.error} />
        {board.isSuccess && (
          <div className="table-responsive">
            <table className="table align-middle mb-0" aria-label="Situações">
              <thead>
                <tr>
                  <th scope="col">Situação</th>
                  <th scope="col">Estado base</th>
                  <th scope="col">Tipo</th>
                  <th scope="col" className="text-end">
                    Posição
                  </th>
                  <th scope="col">
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {statuses.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <StatusBadge tone={s.color}>{s.label}</StatusBadge>
                    </td>
                    <td>{BASE_STATE_LABELS[s.baseState]}</td>
                    <td>{s.isSystem ? "Sistema" : "Customizada"}</td>
                    <td className="text-end">{s.position}</td>
                    <td className="text-end text-nowrap">
                      {can(perms, "statuses", "update") && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary me-2"
                          aria-label={`Editar ${s.label}`}
                          onClick={() =>
                            setEditing({
                              id: s.id,
                              isSystem: s.isSystem,
                              values: {
                                label: s.label,
                                color: s.color,
                                baseState: s.baseState,
                                position: s.position,
                              },
                            })
                          }
                        >
                          Editar
                        </button>
                      )}
                      {can(perms, "statuses", "delete") && !s.isSystem && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          aria-label={`Excluir ${s.label}`}
                          disabled={del.isPending}
                          onClick={() => setDeleting({ kind: "status", id: s.id, label: s.label })}
                        >
                          Excluir
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="box card">
        <div className="px-3 pt-3 pb-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h2 className="h6 mb-0">Transições</h2>
          {can(perms, "statuses", "create") && board.isSuccess && (
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => setEditingTransition("new")}
            >
              Incluir transição
            </button>
          )}
        </div>
        <p className="form-text px-3 mb-2">
          Um documento só muda para as situações listadas aqui. Cancelar sempre estorna estoque e contas;
          voltar de uma situação desfaz o que ela lançou conforme as ações (estorno, nunca exclusão).
        </p>
        {board.isSuccess && transitions.length === 0 && (
          <p className="text-muted p-3 mb-0">Nenhuma transição neste módulo.</p>
        )}
        {board.isSuccess && transitions.length > 0 && (
          <div className="table-responsive">
            <table className="table align-middle mb-0" aria-label="Transições">
              <thead>
                <tr>
                  <th scope="col">De</th>
                  <th scope="col">Para</th>
                  <th scope="col">Ações</th>
                  <th scope="col">
                    <span className="visually-hidden">Ações da linha</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {transitions.map((t) => (
                  <tr key={t.id} data-testid={`transition-${t.id}`}>
                    <td>{badge(t.fromStatusId)}</td>
                    <td>{badge(t.toStatusId)}</td>
                    <td>{describeActions(t.actions)}</td>
                    <td className="text-end text-nowrap">
                      {can(perms, "statuses", "update") && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary me-2"
                          aria-label={`Editar transição ${pairLabel(t)}`}
                          onClick={() => setEditingTransition(t)}
                        >
                          Editar
                        </button>
                      )}
                      {can(perms, "statuses", "delete") && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          aria-label={`Excluir transição ${pairLabel(t)}`}
                          disabled={del.isPending}
                          onClick={() => setDeleting({ kind: "transition", id: t.id, label: pairLabel(t) })}
                        >
                          Excluir
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {editing && (
        <StatusDialog module={module} editing={editing} onClose={() => setEditing(null)} onSaved={changed} />
      )}
      {editingTransition && board.isSuccess && (
        <TransitionDialog
          module={module}
          statuses={statuses}
          transition={editingTransition === "new" ? null : editingTransition}
          onClose={() => setEditingTransition(null)}
          onSaved={changed}
        />
      )}
      <ConfirmDialog
        open={!!deleting}
        title={deleting?.kind === "transition" ? "Excluir transição" : "Excluir situação"}
        message={`Excluir a ${deleting?.kind === "transition" ? "transição" : "situação"} "${
          deleting?.label ?? ""
        }"? Ela pode ser restaurada pela Consulta de exclusões.`}
        confirmLabel="Excluir"
        onConfirm={() => {
          if (deleting) del.mutate(deleting);
          setDeleting(null);
        }}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}

function StatusDialog({
  module,
  editing,
  onClose,
  onSaved,
}: {
  module: StatusModule;
  editing: Editing;
  onClose(): void;
  onSaved(): void;
}) {
  const { api } = useSession();
  const toast = useToast();
  const [open, setOpen] = useState(true);
  const [v, setV] = useState<StatusInput>(editing.values);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: () =>
      api(editing.id ? `/statuses/${editing.id}` : "/statuses", {
        method: editing.id ? "PUT" : "POST",
        body: JSON.stringify(editing.id ? v : { ...v, module }),
      }),
    onSuccess: () => {
      toast("success", "Situação salva");
      onSaved();
      setOpen(false);
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      else if (e instanceof ApiFailure && e.code === "duplicate_status") setErrors({ label: e.message });
      toast("danger", errorMessage(e));
    },
  });
  return (
    <Modal
      open={open}
      title={editing.id ? "Editar situação" : `Nova situação · ${STATUS_MODULE_LABELS[module]}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar
          </button>
        </>
      }
    >
      <div
        className="row"
        onKeyDown={(e) => {
          // O diálogo pode ficar dentro de um <form> de outra tela: Enter não submete nada daqui.
          if (e.key === "Enter" && e.target instanceof HTMLInputElement) e.preventDefault();
        }}
      >
        <Field label="Nome" required className="col-12" error={errors.label}>
          <input
            value={v.label}
            maxLength={40}
            data-autofocus
            onChange={(e) => setV({ ...v, label: e.target.value })}
          />
        </Field>
        <Field label="Cor" className="col-md-6" error={errors.color}>
          <select
            className="form-select"
            value={v.color}
            onChange={(e) => setV({ ...v, color: e.target.value as StatusInput["color"] })}
          >
            {STATUS_TONES.map((t) => (
              <option key={t} value={t}>
                {TONE_LABELS[t]}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Estado base"
          className="col-md-6"
          error={errors.baseState}
          hint={
            editing.isSystem ? "Fixo nas situações do sistema." : "Usado nos relatórios e no cancelamento."
          }
        >
          <select
            className="form-select"
            value={v.baseState}
            disabled={editing.isSystem}
            onChange={(e) => setV({ ...v, baseState: e.target.value as StatusInput["baseState"] })}
          >
            {BASE_STATES.map((b) => (
              <option key={b} value={b}>
                {BASE_STATE_LABELS[b]}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Posição"
          className="col-md-6"
          error={errors.position}
          hint="Ordem nas listas (0 a 999)."
        >
          <input
            type="number"
            min={0}
            max={999}
            value={v.position}
            onChange={(e) => setV({ ...v, position: Number.parseInt(e.target.value || "0", 10) })}
          />
        </Field>
        <div className="col-md-6 mb-3 d-flex align-items-end">
          <span>
            Prévia: <StatusBadge tone={v.color}>{v.label || "Situação"}</StatusBadge>
          </span>
        </div>
      </div>
    </Modal>
  );
}

function TransitionDialog({
  module,
  statuses,
  transition,
  onClose,
  onSaved,
}: {
  module: StatusModule;
  statuses: StatusView[];
  transition: TransitionView | null;
  onClose(): void;
  onSaved(): void;
}) {
  const { api } = useSession();
  const toast = useToast();
  const [open, setOpen] = useState(true);
  const [from, setFrom] = useState(transition?.fromStatusId ?? "");
  const [to, setTo] = useState(transition?.toStatusId ?? "");
  const [actions, setActions] = useState<TransitionAction[]>(transition?.actions ?? []);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const target = statuses.find((s) => s.id === to) ?? null;
  const offered = offeredActions(module, target?.baseState ?? null);
  const chosen = actions.filter((a) => offered.includes(a));
  const save = useMutation({
    mutationFn: () =>
      api(transition ? `/status-transitions/${transition.id}` : "/status-transitions", {
        method: transition ? "PUT" : "POST",
        body: JSON.stringify(
          transition ? { actions: chosen } : { fromStatusId: from, toStatusId: to, actions: chosen },
        ),
      }),
    onSuccess: () => {
      toast("success", "Transição salva");
      onSaved();
      setOpen(false);
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      else if (e instanceof ApiFailure && e.code === "duplicate_transition")
        setErrors({ toStatusId: e.message });
      toast("danger", errorMessage(e));
    },
  });
  const toggle = (a: TransitionAction, on: boolean) =>
    setActions((cur) => (on ? [...cur.filter((x) => x !== a), a] : cur.filter((x) => x !== a)));
  return (
    <Modal
      open={open}
      title={transition ? "Editar transição" : "Nova transição"}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending || !from || !to}
            onClick={() => save.mutate()}
          >
            Salvar
          </button>
        </>
      }
    >
      <div className="row">
        <Field label="De" required className="col-md-6" error={errors.fromStatusId}>
          <select
            className="form-select"
            value={from}
            disabled={!!transition}
            onChange={(e) => setFrom(e.target.value)}
          >
            <option value="">Selecione</option>
            {statuses.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Para" required className="col-md-6" error={errors.toStatusId}>
          <select
            className="form-select"
            value={to}
            disabled={!!transition}
            onChange={(e) => setTo(e.target.value)}
          >
            <option value="">Selecione</option>
            {statuses
              .filter((s) => s.id !== from)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
          </select>
        </Field>
      </div>
      <fieldset>
        <legend className="form-label fs-6">Ações ao mudar de situação</legend>
        {offered.length === 0 && (
          <p className="text-muted mb-2">
            Este módulo não reserva nem lança estoque ou contas: a transição só muda a situação.
          </p>
        )}
        {offered.map((a) => (
          <div className="form-check" key={a}>
            <input
              id={`action-${a}`}
              type="checkbox"
              className="form-check-input"
              checked={chosen.includes(a)}
              onChange={(e) => toggle(a, e.target.checked)}
            />
            <label className="form-check-label" htmlFor={`action-${a}`}>
              {ACTION_LABELS[a]}
            </label>
          </div>
        ))}
        {errors.actions && <div className="invalid-feedback d-block">{errors.actions}</div>}
        {target?.baseState === "canceled" && (
          <div className="form-text">
            Cancelar sempre estorna estoque e contas, mesmo sem marcar as ações.
          </div>
        )}
      </fieldset>
    </Modal>
  );
}
